import { FolderTypeEnum } from "../src/taskpane/components/enums/FolderTypeEnum";
import { ModeEnum } from "../src/taskpane/components/enums/modeEnum";
import { getLastSplittedStringItem, removeExtension } from "../src/taskpane/components/helpers/stringHelper";
import { IFileToUpload } from "../src/taskpane/interfaces/IFileToUpload";
import { apiGetAttachmentExtension } from "../src/taskpane/services/ApiService";
import { FileFolderNameExtensions } from "../src/taskpane/services/FileFolderNameExtensions";
import { getAuthenticatedClient } from "../src/taskpane/services/GraphClientService";
import { OutlookService } from "../src/taskpane/services/OutlookService";
import authModule from "./AuthModule";
import { axiosRetryGet, axiosRetryPatch } from "./AxiosRetryUtil";
import { logger } from "./logger";
const mime = require("mime");

export enum CategoryDisplayNames {
    Teams = "Saved to Teams",
    SharePoint = "Saved to SharePoint",
    OneDrive = "Saved to OneDrive",
}

export interface IMailSettingsDto {
    accountId: string,
    encryptedPayload: string,
}

export class OutlookUtils {
    public static ewsOnlySupport: boolean = false;

    private static readonly savedToTeamsCategory: Office.CategoryDetails = {
        "displayName": CategoryDisplayNames.Teams,
        "color": "Preset8" //Purple
    };

    private static readonly savedToSharePointCategory: Office.CategoryDetails = {
        "displayName": CategoryDisplayNames.SharePoint,
        "color": "Preset11" //Steel
    };

    private static readonly savedToOneDriveCategory: Office.CategoryDetails = {
        "displayName": CategoryDisplayNames.OneDrive,
        "color": "Preset7" //Blue
    };

    static masterCategoriesToAdd: Office.CategoryDetails[] = [
        OutlookUtils.savedToTeamsCategory,
        OutlookUtils.savedToSharePointCategory,
        OutlookUtils.savedToOneDriveCategory
    ];

    public static getOutlookToken = async (): Promise<string> => {
        let outlookToken: string;
        try {
            outlookToken = await new Promise((resolve, reject) => {
                Office.context.mailbox.getCallbackTokenAsync({ isRest: true }, result => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(result.error);
                    }
                });
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getOutlookToken)");
        }
        return outlookToken;
    }

    public static getMailboxAddress = async (): Promise<string> => {
        let mailboxAddress: string;
        try {
            if (Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                // In Windows, Mac, and the web client, this indicates a shared item so use SharedProperties properties to construct the REST URL.
                // Add-ins don't activate on shared items in mobile so no need to handle.
                mailboxAddress = await new Promise<string>((resolve, reject) => {
                    Office.context.mailbox.item.getSharedPropertiesAsync((result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            const sharedProperties: Office.SharedProperties = result.value;
                            resolve(sharedProperties.targetMailbox);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getRestURL)");
        }
        return mailboxAddress;
    }

    public static getComposeModeItemId = async (): Promise<string> => {
        let itemId: string;
        try {
            itemId = await new Promise((resolve, reject) => {
                Office.context?.mailbox?.item?.saveAsync(null, (saveAsyncResult) => {
                    if (saveAsyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(saveAsyncResult.value);
                    } else {
                        reject(saveAsyncResult.error);
                    }
                });
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getComposeModeItemId)");
        }
        return itemId;
    }

    public static getItemTo = async (): Promise<Office.EmailAddressDetails[]> => {
        let to: Office.EmailAddressDetails[];
        try {
            to = await new Promise((resolve, reject) => {
                Office.context?.mailbox?.item?.to?.getAsync(null, (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(asyncResult.value);
                    } else {
                        reject(asyncResult.error);
                    }
                })
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getItemSubject)");
        }
        return to;
    }

    public static getItemSubject = async (): Promise<string> => {
        let subject: string;
        try {
            subject = await new Promise((resolve, reject) => {
                Office.context?.mailbox?.item?.subject?.getAsync(null, (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(asyncResult.value);
                    } else {
                        reject(asyncResult.error);
                    }
                })
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getItemSubject)");
        }
        return subject;
    }

    public static getItemFrom = async (): Promise<string> => {
        let fromEmailAddress: string;
        try {
            fromEmailAddress = await new Promise((resolve, reject) => {
                Office.context?.mailbox?.item?.from?.getAsync(null, (asyncResult) => {
                    if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(asyncResult.value?.emailAddress);
                    } else {
                        reject(asyncResult.error);
                    }
                })
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getItemSubject)");
        }
        return fromEmailAddress;
    }

    public static verifyMailboxAccess = async (verifyGraphAccess: boolean, mode: ModeEnum, callback?: (hasMailboxAccess: boolean) => Promise<void>, depth?: number): Promise<boolean> => {
        let hasAccess: boolean = false;
        try {
            const itemId: string = mode === ModeEnum.mailRead
                ? Office.context?.mailbox?.item?.itemId
                : await this.getComposeModeItemId();

            if (verifyGraphAccess) {
                if (itemId) {
                    if (Office.context?.mailbox?.userProfile?.accountType !== "enterprise") {
                        try {
                            const mailboxResource: string = Office.context?.mailbox?.item?.getSharedPropertiesAsync
                                ? `users/${await this.getMailboxAddress()}`
                                : "me";

                            const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
                            const message = await client.api(`/${mailboxResource}/messages/${OutlookService.getItemRestId(itemId)}`)
                                .select("id")
                                .get();

                            if (message?.id?.trim()) {
                                hasAccess = true;
                            }
                        } catch (error) {
                            if (mode === ModeEnum.mailCompose && Office.context?.mailbox?.diagnostics?.hostName === "Outlook" && (depth || 0) < 10) {
                                await new Promise(resolve => setTimeout(resolve, 2000));
                                return await this.verifyMailboxAccess(true, mode, callback, (depth || 0) + 1);
                            }
                            logger(error, "OutlookUtils.ts (verifyMailboxAccess - Graph)");
                        }
                    }

                    if (!hasAccess) {
                        hasAccess = true;
                        this.ewsOnlySupport = true;

                        // // Try check if ews callback support is enabled
                        // const [restToken, ewsToken, restUrl] = await Promise.all([OutlookUtils.getOutlookToken(), OutlookUtils.getEWSToken(), OutlookUtils.getRestURL()]);
                        // if ((restToken && restUrl) || (ewsToken && OutlookUtils.getEWSURL() && OutlookService.getItemEWSId(itemId))) {
                        //     if (ewsToken && itemId && OutlookUtils.getEWSURL()) {
                        //         // const soapRequest =
                        //         //     '<?xml version="1.0" encoding="utf-8"?>' +
                        //         //     '<soap:Envelope xmlns:xsi="https://www.w3.org/2001/XMLSchema-instance"' +
                        //         //     '               xmlns:xsd="https://www.w3.org/2001/XMLSchema"' +
                        //         //     '               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"' +
                        //         //     '               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">' +
                        //         //     '  <soap:Header>' +
                        //         //     '    <RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0" />' +
                        //         //     '  </soap:Header>' +
                        //         //     '  <soap:Body>' +
                        //         //     '    <GetItem xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">' +
                        //         //     '      <ItemShape>' +
                        //         //     '        <t:BaseShape>IdOnly</t:BaseShape>' +
                        //         //     '      </ItemShape>' +
                        //         //     '      <ItemIds><t:ItemId Id="' + itemId + '"/></ItemIds>' +
                        //         //     '    </GetItem>' +
                        //         //     '  </soap:Body>' +
                        //         //     '</soap:Envelope>';

                        //         // Office.context.mailbox.makeEwsRequestAsync(soapRequest, asyncRes => console.log(asyncRes))
                        //         hasAccess = true;
                        //         this.ewsOnlySupport = true;
                        //     }
                        //     else if (restToken && restUrl) {
                        //         const getMessageIdUrl: string = `${restUrl}/messages/${OutlookService.getItemRestId(itemId)}/?$select=Id`;
                        //         const headers = {
                        //             "Authorization": "Bearer " + restToken
                        //         }

                        //         let messageId: string;
                        //         try {
                        //             messageId = (await axiosRetryGet(getMessageIdUrl, {
                        //                 headers: headers
                        //             }))?.data?.Id;
                        //         } catch (e) {
                        //             logger(e, "OutlookUtils.ts (verifyMailboxAccess - Outlook rest)");
                        //         }

                        //         if (messageId?.trim()) {
                        //             hasAccess = true;
                        //             this.ewsOnlySupport = true;
                        //         }
                        //     }
                        // }
                    }
                }
                else if (Office.context?.mailbox?.getSelectedItemsAsync) {
                    hasAccess = await new Promise(resolve => {
                        Office.context.mailbox.getSelectedItemsAsync(asyncResult => {
                            if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                                resolve(true);
                            }
                            else {
                                logger(Error(asyncResult.error.message), "OutlookUtils.ts (verifyMailboxAccess - getSelectedItemsAsync)");
                                resolve(false);
                            }
                        });
                    });
                }
            }
            else {
                hasAccess = true;
                this.ewsOnlySupport = !Office.context?.mailbox?.userProfile?.accountType || Office.context.mailbox.userProfile.accountType === "enterprise";
            }
        } catch (error) {
            console.warn("VerifyMailboxAccess: " + error);
            logger(error, "OutlookUtils.ts (verifyMailboxAccess)");
        }

        if (callback) {
            await callback(hasAccess);
        }
        return hasAccess;
    }

    public static getDraftMessageDetails = async (autoSelectAttachments?: boolean): Promise<IFileToUpload[]> => {
        try {
            const itemId: string = await OutlookUtils.getComposeModeItemId();
            if (itemId) {
                const messageRestId: string = OutlookService.getItemRestId(itemId);

                const mailboxResource: string = Office.context?.mailbox?.item?.getSharedPropertiesAsync
                    ? `users/${await this.getMailboxAddress()}`
                    : "me";

                const client = getAuthenticatedClient(await authModule.getGraphAccessToken());

                let draftAttachments: any[] = [];
                try {
                    const messagetAttachments = await client.api(`/${mailboxResource}/messages/${messageRestId}/attachments`)
                        .select("id,name,lastModifiedDateTime,isInline,size,contentType")
                        .get();

                    if (Array.isArray(messagetAttachments?.value)) {
                        draftAttachments = messagetAttachments.value
                            .filter(attachment => attachment?.id && !attachment.isInline && attachment.size > 0)
                            .map(attachment => {
                                return {
                                    id: attachment.id,
                                    ewsId: null,
                                    parentId: messageRestId,
                                    name: removeExtension(FileFolderNameExtensions.EnsureValidName(attachment.name)),
                                    extension: OutlookUtils.getExtension(attachment),
                                    isSelected: autoSelectAttachments === true,
                                    isAttachment: true,
                                    isDuplicate: false,
                                    contentType: attachment.contentType,
                                    sendOnSave: true
                                };
                            })
                            .filter(attachment => attachment?.id);
                    }
                } catch (error) {
                    logger(error, "OutlookUtils.ts (draftAttachments)");
                }

                const mainMailItem: IFileToUpload = {
                    id: messageRestId,
                    ewsId: OutlookService.getItemEWSId(itemId),
                    name: FileFolderNameExtensions.EnsureValidName(await OutlookUtils.getItemSubject()),
                    extension: "eml",
                    isSelected: true,
                    isAttachment: false,
                    isDuplicate: false,
                    isMainMailItem: true,
                    sendOnSave: true
                };

                return [mainMailItem, ...draftAttachments];
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getExchangeMessageAttachments)");
        }
        return [];
    }

    public static getDraftMassage = async (): Promise<any | undefined> => {
        try {
            const itemId: string = await this.getComposeModeItemId();
            if (itemId) {
                const mailboxResource: string = Office.context?.mailbox?.item?.getSharedPropertiesAsync
                    ? `users/${await this.getMailboxAddress()}`
                    : "me";

                const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
                return await client.api(`/${mailboxResource}/messages/${OutlookService.getItemRestId(itemId)}`)
                    .get();
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getDraftMassage)");
        }
        return undefined;
    }

    public static sendDraft = async (): Promise<void> => {
        try {
            const itemId: string = await this.getComposeModeItemId();
            if (itemId) {
                const mailboxResource: string = Office.context?.mailbox?.item?.getSharedPropertiesAsync
                    ? `users/${await this.getMailboxAddress()}`
                    : "me";

                let sendBody;
                const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
                await client.api(`/${mailboxResource}/messages/${OutlookService.getItemRestId(itemId)}/send`)
                    .post(sendBody);

                setTimeout(() => {
                    if (Office.context?.requirements?.isSetSupported("Mailbox", "1.14")) {
                        Office.context.mailbox?.item?.closeAsync(
                            (asyncResult) => {
                                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                                    logger(new Error("Close draft compose surface failed with error: " + asyncResult.error.message), "OutlookUtils.ts (sendDraft)");
                                }
                            });
                    }
                    else {
                        Office.context?.mailbox?.item?.close();
                    }
                }, 1250);
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (sendDraft)");
        }
    }

    public static getRestURL = async (): Promise<string> => {
        let restURL: string;
        try {
            if (Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                // In Windows, Mac, and the web client, this indicates a shared item so use SharedProperties properties to construct the REST URL.
                // Add-ins don't activate on shared items in mobile so no need to handle.
                restURL = await new Promise<string>((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        reject("getSharedPropertiesAsync timeout");
                    }, 1500);
                    Office.context.mailbox.item.getSharedPropertiesAsync((result) => {
                        clearTimeout(timeout);
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            const sharedProperties: Office.SharedProperties = result.value;
                            resolve(sharedProperties.targetRestUrl + "/v2.0/users/" + sharedProperties.targetMailbox);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getRestURL)");
        }

        if (!restURL) {
            restURL = Office.context.mailbox.restUrl + "/v2.0/users/" + Office.context.mailbox.userProfile.emailAddress;
        }

        return restURL;
    }

    public static getEWSToken = async (): Promise<string> => {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
            return null;
        }

        let ewsToken: string;
        try {
            ewsToken = await new Promise((resolve, reject) => {
                Office.context.mailbox.getCallbackTokenAsync(result => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(result.error);
                    }
                });
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getEWSToken)");
        }
        return ewsToken;
    }

    public static getEWSURL = (): string => {
        const hostName: string = Office.context?.mailbox?.diagnostics?.hostName;
        if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
            return null;
        }
        return Office.context.mailbox.ewsUrl;
    }

    public static getMailItemHTMLBody = async (mailItem: Office.Item & Office.MessageCompose & Office.MessageRead & Office.AppointmentCompose & Office.AppointmentRead) => {
        if (mailItem?.body) {
            try {
                return await new Promise<string>((resolve, reject) => {
                    mailItem?.body?.getAsync(Office.CoercionType.Html, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            resolve(result.value);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            } catch (error) {
                logger(error, "OutlookUtils.ts (getMailItemHTMLBody)");
            }
        }
        return null;
    }

    public static getExtension = (attachment: Office.AttachmentDetails) => {
        try {
            if (attachment.name?.includes(".")) {
                return getLastSplittedStringItem(attachment.name, /[\s.]+/);
            }
            else if (attachment.contentType === "message/rfc822") {
                return "eml";
            }
            else if (attachment.contentType) {
                return mime.getExtension(attachment.contentType);
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getExtension)", attachment?.name);
        }
        return "";
    }

    public static requestExtensions = async (ewsUrl: string, ewsToken: string, attachmentIdList: string[]) => {
        try {
            return await apiGetAttachmentExtension(ewsUrl, ewsToken, attachmentIdList);
        } catch (e) {
            logger(e, "OutlookUtils.ts (requestExtensions)");
        }
        return null;
    }

    public static getCategoryDetails = (categoryType: FolderTypeEnum): Office.CategoryDetails | undefined => {
        let categoryToSet: Office.CategoryDetails;
        switch (categoryType) {
            case FolderTypeEnum.teamChannel:
                categoryToSet = OutlookUtils.savedToTeamsCategory;
                break;
            case FolderTypeEnum.sharepoint:
                categoryToSet = OutlookUtils.savedToSharePointCategory;
                break;
            case FolderTypeEnum.oneDrive:
                categoryToSet = OutlookUtils.savedToOneDriveCategory;
                break;
            default:
                break;
        }
        return categoryToSet;
    }

    private static getMasterCategories = async (): Promise<Office.CategoryDetails[]> => {
        try {
            return await new Promise<Office.CategoryDetails[]>((resolve, reject) => {
                Office.context.mailbox.masterCategories.getAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(result.error);
                    }
                });
            });
        } catch (error) {
            logger(error, "OutlookUtils.ts (getMasterCategories)");
            return null;
        }
    }

    private static addMasterCategories = async (categories: Office.CategoryDetails[]): Promise<boolean> => {
        try {
            // Api not supported for shared items.
            if (!Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
                return await new Promise<boolean>((resolve, reject) => {
                    Office.context.mailbox.masterCategories.addAsync(categories, null, (result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            resolve(true);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (addMasterCategories)");
        }
        return false;
    }

    public static getItemCategories = async (): Promise<Office.CategoryDetails[]> => {
        try {
            if (Office.context.requirements.isSetSupported("Mailbox", "1.8") && Office.context.mailbox?.item?.itemId) {
                return await new Promise<Office.CategoryDetails[]>((resolve, reject) => {
                    Office.context.mailbox.item.categories.getAsync((result) => {
                        if (result.status === Office.AsyncResultStatus.Succeeded) {
                            resolve(result.value);
                        } else {
                            reject(result.error);
                        }
                    });
                });
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (getItemCategories)");
        }
        return [];
    }

    public static ensureMasterCategoryExists = async (category: Office.CategoryDetails): Promise<boolean> => {
        if (Office.context.requirements.isSetSupported("Mailbox", "1.8")) {
            const currentCategories: Office.CategoryDetails[] = await OutlookUtils.getMasterCategories();
            const exists: boolean = currentCategories?.some(c => c.displayName?.trim().toLowerCase() === category.displayName?.trim().toLowerCase());
            if (exists === true || (await OutlookUtils.addMasterCategories([category]))) {
                return true;
            }
        }
        return false;
    }

    //You can apply a category to an item by assigning the displayName property of the category to the categories collection of the item.
    //https://docs.microsoft.com/en-us/graph/api/resources/outlookcategory?view=graph-rest-1.0
    //FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel
    public static applyCategory = async (itemId: string, categoryType: FolderTypeEnum) => {
        try {
            let categories: string[];

            if (this.ewsOnlySupport) {
                const token = await OutlookUtils.getOutlookToken();
                const selectors = "/?$select=categories"
                const getMessageUrl = await OutlookUtils.getRestURL() + "/messages/" + itemId + selectors;
                const headers = {
                    "Authorization": "Bearer " + token
                }
                try {
                    categories = (await axiosRetryGet(getMessageUrl, {
                        headers: headers
                    }))?.data.Categories;
                } catch (e) {
                    logger(e, "OutlookUtils.ts (applyCategory 1)");
                }

                if ((categories.includes(CategoryDisplayNames.Teams) && categoryType === FolderTypeEnum.teamChannel)
                    || (categories.includes(CategoryDisplayNames.SharePoint) && categoryType === FolderTypeEnum.sharepoint)) {
                    return; //no need for update or going further if category is already included 
                }

                const categoryDetails: Office.CategoryDetails = OutlookUtils.getCategoryDetails(categoryType);
                if (categoryDetails) {
                    categories.push(categoryDetails.displayName);

                    const updateMessageUrl = await OutlookUtils.getRestURL() + "/messages/" + itemId;
                    const data = {
                        Categories: categories
                    };
                    try {
                        const result = (await axiosRetryPatch(updateMessageUrl, data, {
                            headers: {
                                "Authorization": "Bearer " + token,
                                "Content-Type": "application/json"
                            }
                        }))?.data;
                    } catch (e) {
                        logger(e, "OutlookUtils.ts (applyCategory 2)");
                    }
                }
            }
        } catch (error) {
            logger(error, "OutlookUtils.ts (applyCategory 3)");
        }
    }

    //categoryType: FolderTypeEnum.sharepoint | FolderTypeEnum.oneDrive | FolderTypeEnum.teamChannel
    public static getCategoriesToAssign = async (itemId: string, categoryType: FolderTypeEnum): Promise<string[]> => {
        if (Office.context.requirements.isSetSupported("Mailbox", "1.8")) {
            try {
                let categories: string[];

                if (this.ewsOnlySupport) {
                    const token = await OutlookUtils.getOutlookToken();
                    const selectors = "/?$select=categories"
                    const getMessageUrl = await OutlookUtils.getRestURL() + "/messages/" + itemId + selectors;
                    const headers = {
                        "Authorization": "Bearer " + token
                    }

                    try {
                        categories = (await axiosRetryGet(getMessageUrl, {
                            headers: headers
                        }))?.data.Categories;
                    } catch (e) {
                        logger(e, "OutlookUtils.ts (getCategoriesToAssign 1)");
                    }
                }
                else {
                    const mailboxResource: string = Office.context?.mailbox?.item?.getSharedPropertiesAsync
                        ? `users/${await this.getMailboxAddress()}`
                        : "me";

                    const client = getAuthenticatedClient(await authModule.getGraphAccessToken());
                    const categoriesRequestResult = await client.api(`/${mailboxResource}/messages/${OutlookService.getItemRestId(itemId)}`)
                        .select("categories")
                        .get();

                    if (Array.isArray(categoriesRequestResult?.categories)) {
                        categories = categoriesRequestResult.categories;
                    }
                }

                if (categories) {
                    if (
                        (categories.some(c => c.trim().toLowerCase() === CategoryDisplayNames.Teams.toLowerCase()) && categoryType === FolderTypeEnum.teamChannel)
                        || (categories.some(c => c.trim().toLowerCase() === CategoryDisplayNames.SharePoint.toLowerCase()) && categoryType === FolderTypeEnum.sharepoint)
                        || (categories.some(c => c.trim().toLowerCase() === CategoryDisplayNames.OneDrive.toLowerCase()) && categoryType === FolderTypeEnum.oneDrive)
                    ) {
                        return null;
                    }
                }
                else {
                    categories = [];
                }

                const categoryToSet: Office.CategoryDetails = OutlookUtils.getCategoryDetails(categoryType);

                if (categoryToSet && await OutlookUtils.ensureMasterCategoryExists(categoryToSet)) {
                    categories.push(categoryToSet.displayName);
                    return categories;
                }
            } catch (error) {
                logger(error, "OutlookUtils.ts (getCategoriesToAssign 2)");
            }
        }
        return null;
    }

    public static setEWSItemCategory = (ewsItemId: string, categoriesToAssign: string[]) => {
        // makeEwsRequestAsync api not supported for shared items.
        if (categoriesToAssign?.length > 0 && ewsItemId && !Office.context?.mailbox?.item?.getSharedPropertiesAsync) {
            const ewsCategorySoapRequest: string = `<?xml version="1.0" encoding="utf-8"?>
            <soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages" xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types">
              <soap:Header>
                <t:RequestServerVersion Version="Exchange2013" xmlns="http://schemas.microsoft.com/exchange/services/2006/types" soap:mustUnderstand="0" />
              </soap:Header>
              <soap:Body>
                <UpdateItem MessageDisposition="SaveOnly" ConflictResolution="AlwaysOverwrite" xmlns="http://schemas.microsoft.com/exchange/services/2006/messages">
                  <ItemChanges>
                    <t:ItemChange>
                      <t:ItemId Id="${ewsItemId}" />
                      <t:Updates>
                        <t:SetItemField>
                          <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray" />
                          <t:Message>
                            <t:ExtendedProperty>
                              <t:ExtendedFieldURI PropertySetId="00020329-0000-0000-C000-000000000046" PropertyName="Keywords" PropertyType="StringArray" />
                              <t:Values>
                                ${categoriesToAssign.map(category => `<t:Value>${category}</t:Value>`).join()}
                              </t:Values>
                            </t:ExtendedProperty>
                          </t:Message>
                        </t:SetItemField>
                      </t:Updates>
                    </t:ItemChange>
                  </ItemChanges>
                </UpdateItem>
              </soap:Body>
            </soap:Envelope>`;
            Office.context.mailbox?.makeEwsRequestAsync(ewsCategorySoapRequest, asyncRes => console.log(asyncRes));
        }
    }
}